import React, { useId, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowRightLeft, History, PhoneCall, StickyNote, UserCheck } from 'lucide-react';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import { addLeadNote } from '../../api/leads';
import { useI18n } from '../../i18n/I18nContext';
import { errorText } from '../../utils/errorText';
import { formatDateTime, formatRelative } from '../../utils/datetime';
import { OUTCOME_TONE } from './leadUi';

const ICON = { CALL: PhoneCall, NOTE: StickyNote, STAGE_CHANGE: ArrowRightLeft, SYSTEM: UserCheck };
const TONE = { CALL: 'brand', NOTE: 'info', STAGE_CHANGE: 'purple', SYSTEM: 'teal' };

/** What one history entry says. Status and owner changes are stored as codes, never as text. */
function entryText(t, entry, nameOf) {
  if (entry.type === 'STAGE_CHANGE') {
    const [, from, to] = String(entry.subject || '').split(':');
    return t('leads.timeline.statusChange', {
      from: from ? t(`leads.status.${from}`) : '—',
      to: to ? t(`leads.status.${to}`) : '—',
    });
  }
  if (entry.type === 'SYSTEM') {
    const userId = String(entry.subject || '').replace(/^ASSIGNED:/, '');
    return userId
      ? t('leads.timeline.assigned', { name: nameOf(userId) || t('leads.timeline.someone') })
      : t('leads.timeline.unassigned');
  }
  if (entry.type === 'CALL') return t('leads.timeline.call');
  return t('leads.timeline.note');
}

/**
 * The lead's history (newest first): calls with their outcome and note, notes, status and owner
 * changes, who did each and when. A note can be added at the top.
 *
 *   <LeadTimeline leadId={id} activities={detail.activities} people={staff} onChanged={setDetail} />
 */
export default function LeadTimeline({ leadId, activities = [], people = [], onChanged }) {
  const { t } = useI18n();
  const uid = useId();
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const names = new Map(people.map((p) => [p.id, p.name]));
  const nameOf = (userId) => names.get(userId);

  const submit = async (e) => {
    e.preventDefault();
    const text = note.trim();
    if (!text) return;
    setSaving(true);
    setError('');
    try {
      const data = await addLeadNote(leadId, text);
      setNote('');
      toast.success(t('leads.note.added'));
      onChanged(data);
    } catch (err) {
      setError(errorText(err, t('leads.note.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="section-card lead-history" aria-labelledby={`${uid}-title`}>
      <div className="lead-card-head">
        <span className="ui-icon-chip ui-icon-chip--md" data-tone="info" aria-hidden="true"><History /></span>
        <h2 className="lead-card-title" id={`${uid}-title`}>{t('leads.timeline.title')}</h2>
      </div>

      <form className="lead-note-form" onSubmit={submit} noValidate>
        <label className="visually-hidden" htmlFor={`${uid}-note`}>{t('leads.note.label')}</label>
        <textarea
          id={`${uid}-note`}
          rows={2}
          maxLength={2000}
          value={note}
          placeholder={t('leads.note.placeholder')}
          onChange={(e) => setNote(e.target.value)}
        />
        <button type="submit" className="ds-btn ds-btn-secondary" disabled={saving || !note.trim()}>{t('leads.note.add')}</button>
      </form>
      {error && <p className="ui-error" role="alert">{error}</p>}

      {activities.length === 0 ? (
        <InlineEmpty icon={<History />} tone="neutral" title={t('leads.timeline.empty')} />
      ) : (
        <ol className="lead-timeline">
          {activities.map((entry) => {
            const Icon = ICON[entry.type] || StickyNote;
            return (
              <li key={entry.id} className={`lead-entry lead-entry--${String(entry.type).toLowerCase()}`}>
                <span className="ui-icon-chip ui-icon-chip--sm" data-tone={TONE[entry.type] || 'neutral'} aria-hidden="true"><Icon /></span>
                <div className="lead-entry__body">
                  <p className="lead-entry__title">
                    <span>{entryText(t, entry, nameOf)}</span>
                    {entry.type === 'CALL' && entry.outcome && (
                      <StatusBadge tone={OUTCOME_TONE[entry.outcome] || 'neutral'}>{t(`leads.outcome.${entry.outcome}`)}</StatusBadge>
                    )}
                  </p>
                  {entry.body && <p className="lead-entry__text">{entry.body}</p>}
                  <p className="lead-entry__meta">
                    {entry.createdBy?.name || t('leads.timeline.someone')}
                    {' · '}
                    <time dateTime={entry.createdAt} title={formatDateTime(entry.createdAt, { year: 'auto' })}>{formatRelative(entry.createdAt)}</time>
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
